import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { contractService, divisionService, supplierService } from '@services';
import type { FilterOption } from '@app-types';
import { REFERENCE_LIST_KEYS } from '@utils/referenceCatalog';
import { useReferenceCatalog } from '@hooks/useReferenceCatalog';

/**
 * Selectable values for the Contract list's selection filters.
 *
 * No new endpoint is needed: Status and Scope are fixed business sets, and the
 * other three already have lookup endpoints. Division and Tier filter by ID,
 * because a name substring is both wrong and ambiguous once the user is picking
 * from a list.
 */

/** Contract lifecycle statuses. Matches CONTRACT_STATUS on the backend. */
const STATUS_OPTIONS: FilterOption[] = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' }
];

/**
 * Assessment scope. Exactly the two values ever stored as a scope.
 *
 * "Calculate" is deliberately absent: it is an assessment *mode* held in
 * assessment_scope_mode, is never persisted as a scope, and filtering by it
 * would return nothing forever. The backend rejects it with a 400.
 */
const SCOPE_OPTIONS: FilterOption[] = [
  { value: 'Include', label: 'Include' },
  { value: 'Exclude', label: 'Exclude' }
];

interface ContractFilterOptions {
  status: FilterOption[];
  scope: FilterOption[];
  division: FilterOption[];
  tier: FilterOption[];
  contractType: FilterOption[];
}

/** Option sets change rarely, so one fetch per session is enough. */
const OPTIONS_STALE_TIME = 5 * 60 * 1000;

export function useContractFilterOptions(): ContractFilterOptions {
  const { lists } = useReferenceCatalog();
  const { data: divisions } = useQuery({
    queryKey: ['divisions', 'active'],
    queryFn: () => divisionService.getActive(),
    staleTime: OPTIONS_STALE_TIME
  });

  const { data: tiers } = useQuery({
    queryKey: ['suppliers', 'tiers'],
    queryFn: () => supplierService.getSupplierTiers(),
    staleTime: OPTIONS_STALE_TIME
  });

  const { data: contractTypes } = useQuery({
    queryKey: ['contracts', 'contract-types'],
    queryFn: () => contractService.getContractTypes(),
    staleTime: OPTIONS_STALE_TIME
  });

  return useMemo(
    () => ({
      status: STATUS_OPTIONS.map((option) => ({
        ...option,
        label:
          lists.find((list) => list.key === REFERENCE_LIST_KEYS.CONTRACT_STATUS)?.values.find((value) => value.code === option.value)?.label
          || option.label,
      })),
      scope: SCOPE_OPTIONS.map((option) => ({
        ...option,
        label:
          lists.find((list) => list.key === REFERENCE_LIST_KEYS.SCOPE)?.values.find((value) => value.code === option.value)?.label
          || option.label,
      })),
      // Division and Tier match by id (FR-004a), so the value is the id.
      division: (divisions ?? []).map((d) => ({ value: String(d.id), label: d.name })),
      tier: (tiers ?? []).map((t) => ({ value: String(t.id), label: t.name })),
      contractType: (contractTypes ?? []).map((type) => ({ value: type, label: type }))
    }),
    [divisions, tiers, contractTypes, lists]
  );
}
